import { getThemepalette } from 'config/theme'
import styled, { css, createGlobalStyle } from 'styled-components'

/**
 * The assessment and assignment shell should stay on the light palette even
 * when the app theme is dark, while contrast classes still handle the
 * accessible reading variants inside the content panes.
 */
const lightContentTheme = getThemepalette('light')

export const ContrastContentArea = createGlobalStyle`
  ${(props) =>
    props.theme.contrast.id !== 'black_on_white' &&
    css`
      .contrast-area {
        &,
        /**
         * Preserve authored table fills and text instead of applying the surrounding contrast preset.
         */
        *:not(.highlighted, .preserve-styles, table, table *):not([data-highlighted='true']) {
          color: ${props.theme.contrast.text.primary} !important;
          background: ${props.theme.contrast.background.primary} !important;
          -webkit-text-fill-color: currentColor !important;
        }

        .highlighted,
        [data-highlighted='true'] {
          background-color: #ffff7b !important;

          /**
           * Yellow highlights need black text in contrast modes where the
           * selected contrast text color is not readable on the highlight.
           */
          ${[
            'white_on_black',
            'yellow_on_red',
            'yellow_on_dark_green',
            'light_pink_on_dark_blue',
            'yellow_on_dark_blue'
          ].includes(props.theme.contrast.id) &&
          css`
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
          `}
        }
      }

      .contrast-area-secondary {
        &,
        /**
         * Preserve authored table fills and text instead of applying the surrounding contrast preset.
         */
        *:not(.highlighted, .preserve-styles, table, table *):not([data-highlighted='true']) {
          color: ${props.theme.contrast.text.secondary} !important;
          background: ${props.theme.contrast.background.secondary} !important;
          -webkit-text-fill-color: currentColor !important;
        }

        .highlighted,
        [data-highlighted='true'] {
          background-color: #ffff7b !important;

          /**
           * Keep highlighted text readable in the same four contrast modes
           * when the highlight is rendered inside secondary content.
           */
          ${[
            'yellow_on_red',
            'yellow_on_dark_green',
            'light_pink_on_dark_blue',
            'yellow_on_dark_blue'
          ].includes(props.theme.contrast.id) &&
          css`
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
          `}
        }
      }

      /**
       * Stimulus images need a visual inversion for the two dark contrast
       * presets where the surrounding content uses a dark background. Keep
       * this selector scoped to the stimulus hook so question images and
       * other assessment images retain their authored appearance.
       */
      ${['white_on_black', 'yellow_on_dark_blue'].includes(
        props.theme.contrast.id
      ) &&
      css`
        .stimulus-content-area.contrast-area img {
          filter: invert(1);
        }
      `}
    `}
`

const defaultTextStyles = css`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: 16px;
  font-family: ${(props) => props.theme.typography.fontInter};
`

export const ObjectiveNameLabel = styled.span`
  ${defaultTextStyles}
`

export const AbsoluteViewModeContainer = styled.div`
  width: 100%;
  position: absolute;
`

export const FooterActionsContainer = styled.div`
  z-index: 10;
  gap: 9px;
  display: flex;
  justify-content: flex-end;
  padding: 13px;
`

export const AssessmentContainer = styled.div`
  height: 100vh;
  width: 100%;
  background-color: ${(props) => props.theme.colors.card.surface};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const PageContent = styled.div`
  width: 100%;
  max-width: 1460px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

export const WorkspaceTitle = styled.span`
  color: ${(props) => props.theme.colors.text.paragraph};
  font-size: 13px;
  font-family: ${(props) => props.theme.typography.fontNunito};
`

export const HeaderContainer = styled.header`
  border-bottom: ${(props) => `1px solid ${props.theme.colors.border.primary}`};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 60px;
  flex-shrink: 0;
  background-color: ${(props) => props.theme.colors.card.background};
`

export const UserDropDownContainer = styled.div`
  background-color: transparent;
`

export const QuestionPill = styled.button`
  height: 44px;
  gap: 16px;
  min-width: 240px;
  max-width: 390px;
  width: 100%;
  border-radius: 999px;
  background-color: ${(props) => props.theme.colors.card.background};
  border: solid 1px ${(props) => props.theme.colors.border.primary};
  padding-left: 20px;
  padding-right: 19px;
  align-items: center;
  justify-content: space-between;
  display: flex;
  text-align: left;
`

export const QuestionTopRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

export const WorkspaceStatus = styled.span`
  color: ${(props) => props.theme.colors.text.paragraph};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

export const ChevronIcon = styled.img`
  width: 18px;
  height: 10px;
`

export const QuestionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const MainContent = styled.main`
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  background-color: ${lightContentTheme.colors.surface.muted};
  background-color: ${lightContentTheme.colors.surface.muted};
`

export const ContentWrapper = styled.div`
  width: 100%;
  max-width: none;
  background-color: ${lightContentTheme.colors.card.background};
  display: flex;
  gap: 0;
  height: 100%;
  min-height: 0;
`

export const FooterContainer = styled.footer`
  height: 60px;
  background-color: ${(props) => props.theme.colors.card.background};
  border-top: ${(props) => `1px solid ${props.theme.colors.border.primary}`};
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  /* Marking this higher than 100 because when marker is active it makes the canvas 99 zIndex
  but the cursor needs to revert when hovering over footer so tools can be interacted with */
  z-index: 120;
  width: 100%;
`

export const ReadingPanel = styled.section<{ $fullWidth: boolean }>`
  width: ${(props) => (props.$fullWidth ? '100%' : '50%')};
  flex-shrink: 0;
  min-width: 0;
  min-height: 0;
  background: ${lightContentTheme.colors.card.background};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const TabsHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: ${lightContentTheme.colors.surface.muted};
  height: 60px;
  min-height: 60px;
  max-height: 60px;
`

export const TabsGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid ${(props) => props.theme.colors.border.secondary};
  flex-grow: 1;
  height: 60px;
  min-height: 60px;
`

export const TabButton = styled.button<{ $active?: boolean }>`
  border: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border.secondary};
  width: 100%;
  padding: 0;
  height: 60px;
  min-height: 60px;
  border-radius: 0;
  font-size: 24px;
  font-family: Nunito;
  font-weight: 400;
  line-height: normal;
  cursor: pointer;
  background: ${(props) =>
    props.$active ? props.theme.colors.surface.infoDark : '#ffffff'};
  color: ${(props) =>
    props.$active ? '#fff' : props.theme.colors.text.primary};
  transition: background-color 0.2s ease;
`

export const TabButtonContent = styled.span`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

export const TabHeaderActionIcon = styled.img`
  position: absolute;
  right: 14px;
  width: 28px;
  height: 28px;
  cursor: pointer;
`

export const RestorePanelButton = styled.button`
  width: 60px;
  height: 60px;
  min-height: 60px;
  border: 0;
  border-left: 1px solid ${(props) => props.theme.colors.border.secondary};
  background: ${(props) => props.theme.colors.card.background};
  padding: 0;
  border-radius: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

export const RestorePanelIcon = styled.img`
  width: 28px;
  height: 28px;
`

export const InstructionBanner = styled.div`
  width: 100%;
  height: 68px;
  gap: 5px;
  background: ${(props) => props.theme.colors.skeleton};
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
`

export const InstructionBannerContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
`

export const InstructionText = styled.span`
  p {
    color: #316bf4;
    font-family: ${(props) => props.theme.typography.fontNunito};
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    padding: 0;
    margin: 0;
  }
`

export const ReadingScroll = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;

  #passage_1,
  #passage_2 {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  #passage_1 img,
  #passage_2 img,
  #passage_1 iframe,
  #passage_2 iframe,
  #passage_1 video,
  #passage_2 video {
    max-width: 100%;
    height: auto;
  }

  #passage_1 table,
  #passage_2 table {
    width: 100% !important;
    max-width: 100%;
    table-layout: fixed;
  }

  #passage_1 td,
  #passage_2 td,
  #passage_1 th,
  #passage_2 th {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`

export const SelectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.text.primary};
  text-align: center;
  font-family: 'Nunito', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`

export const CalculatorOverlay = styled.div`
  position: fixed;
  left: 350px;
  bottom: 120px;
  z-index: 140;
  display: none;
  pointer-events: auto;
`

export const CalculatorPanel = styled.div`
  position: relative;
  background: ${(props) => props.theme.colors.card.background};
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
`

export const CalculatorCloseButton = styled.button`
  position: absolute;
  top: -14px;
  right: -14px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  z-index: 1;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
  }
`

export const CalculatorHost = styled.div`
  display: none;
  cursor: move;
`

export const SelectionParagraph = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.primary};
  opacity: 0.8;
  line-height: 1.45;
  font-size: 15px;
`

export const QuestionPanel = styled.aside<{ $fullWidth?: boolean }>`
  width: ${(props) => (props.$fullWidth ? '100%' : '50%')};
  flex-shrink: 0;
  background: ${lightContentTheme.colors.card.background};
  padding: 10px 12px;
  display: flex;
  flex-direction: ${(props) => (props.$fullWidth ? 'row' : 'column')};
  min-width: 0;
  overflow-y: auto;
`

export const QuestionPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

export const QuestionTitle = styled.div`
  display: flex;
  gap: 5px;

  p {
    color: #000000;
    font-family: ${(props) => props.theme.typography.fontNunitoSans};
    font-size: 19px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }
`

export const QuestionBody = styled.div``

export const HideAllButton = styled.button`
  border-radius: 8px;
  border: 1px solid ${lightContentTheme.colors.border.primary};
  background: ${lightContentTheme.colors.card.background};
  width: 87px;
  height: 33px;
  box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.25);
  cursor: pointer;
`

export const OptionVisibilityToggle = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  margin-right: 10px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

export const OptionVisibilityIcon = styled.img`
  width: 18px;
  height: 18px;
`

export const OptionList = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const ChoiceOption = styled.div<{ $filled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 27px;
  height: 27px;
  color: #537ab7;
  border-radius: 50%;
  font-family: 'Helvetica Neue';
  font-size: 20px;
  line-height: unset;
  cursor: pointer;

  ${(props) =>
    props.$filled
      ? `
  color: #fff;
  background-color: #316BF4;
  `
      : `
    border: 2px solid #537ab7;
    
    `}
`

export const FooterObjectiveTitle = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin-left: 10px;
`

export const ActionButtonStyled = styled.button<{
  $variant?: string
  $width?: number
  disabled?: boolean
}>`
  width: ${(props) => (props.$width ? `${props.$width}px` : '79px')};
  height: 33px;
  outline: none;
  border: ${(props) =>
    props.$variant === 'clear'
      ? `1px solid ${props.theme.colors.border.primary}`
      : 'none'};
  border-radius: 40px;
  background-color: ${(props) => {
    switch (props.$variant) {
      case 'warn':
        return props.theme.colors.surface.warn
      case 'info':
        return props.theme.colors.surface.info
      case 'info-dark':
        return props.theme.colors.surface.infoDark
      case 'success':
        return props.theme.colors.surface.success
      case 'error':
        return props.theme.colors.surface.errorDark
      case 'clear':
        return '#ffffff'
      default:
        return props.theme.colors.surface.muted
    }
  }};
  color: ${(props) =>
    props.$variant === 'warn' || props.$variant === 'clear' ? '#000' : '#fff'};
  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;
  `};
`

export const AssessmentHeaderRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
export const TextToSpeechContainer = styled.div`
  display: flex;
  gap: 9px;
  margin-right: 50px;
  align-items: center;
  justify-content: center;
`

export const TextToSpeechText = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  align-self: flex-start;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

export const ContentArea = styled.div<{ $width?: string }>`
  width: ${(props) => (props.$width ? `${props.$width}` : '1200px')};
  overflow-y: auto;
  height: 100%;

  .cms-content-area {
    p {
      margin: 0;
      font-size: 19px;
      color: #707070;
    }
  }
  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: 100%;
  }
`
